// ───────── Formatters ─────────

export const formatCurrency = (v, currency = "COP") => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency, maximumFractionDigits: 0,
  }).format(num)
}

export const formatNumber = (v, decimals = 0) => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: decimals, maximumFractionDigits: decimals,
  }).format(num)
}

export const formatPercent = (v, decimals = 1) => {
  const num = Number(v)
  if (!isFinite(num)) return "—"
  return `${num.toFixed(decimals)}%`
}

export const formatGrowth = v => {
  const num = Number(v)
  if (!isFinite(num)) return { text: "—", color: "#64748b", icon: "minus" }
  const text = `${num > 0 ? "+" : ""}${num.toFixed(1)}%`
  if (num > 0) return { text, color: "#10b981", icon: "trending-up" }
  if (num < 0) return { text, color: "#ef4444", icon: "trending-down" }
  return { text, color: "#64748b", icon: "minus" }
}

export const formatFecha = iso => {
  if (!iso) return "—"
  try {
    const s = String(iso).substring(0, 10)
    const [y, m, d] = s.split("-")
    if (y && m && d) return `${d}/${m}/${y}`
    return iso
  } catch { return iso }
}

export const formatFechaLarga = iso => {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "long", year: "numeric" })
  } catch { return iso }
}

// ───────── Paleta de segmentos RFM (alineada con el back) ─────────

export const SEGMENTOS = {
  champion: {
    label: "Champion", emoji: "🏆", color: "#16a34a", bg: "#dcfce7",
    desc: "Top — fidelizar con programa premium y atención personalizada",
  },
  loyal: {
    label: "Loyal", emoji: "💚", color: "#22c55e", bg: "#dcfce7",
    desc: "Cuidar — programa de retención y lealtad",
  },
  potential_loyal: {
    label: "Potential Loyal", emoji: "🌱", color: "#84cc16", bg: "#ecfccb",
    desc: "Convertir — incentivos para volverlo recurrente",
  },
  new: {
    label: "New", emoji: "✨", color: "#3b82f6", bg: "#dbeafe",
    desc: "Onboarding — primera impresión y descuento 2da compra",
  },
  promising: {
    label: "Promising", emoji: "💛", color: "#84cc16", bg: "#ecfccb",
    desc: "Engancharlo — comunicación frecuente",
  },
  needs_attention: {
    label: "Needs Attention", emoji: "⚠️", color: "#eab308", bg: "#fef9c3",
    desc: "Comunicación proactiva — antes de que se vaya",
  },
  at_risk: {
    label: "At Risk", emoji: "🟠", color: "#f97316", bg: "#ffedd5",
    desc: "Recuperar — campañas urgentes",
  },
  cant_lose: {
    label: "Can't Lose", emoji: "🔴", color: "#ef4444", bg: "#fee2e2",
    desc: "Atención CEO/comercial — cliente histórico desconectándose",
  },
  hibernating: {
    label: "Hibernating", emoji: "💤", color: "#dc2626", bg: "#fee2e2",
    desc: "Descuento agresivo (15-25%) con producto top",
  },
  lost: {
    label: "Lost", emoji: "⚫", color: "#7f1d1d", bg: "#f1f5f9",
    desc: "Última oportunidad — ofertas especiales",
  },
  regular: {
    label: "Regular", emoji: "👤", color: "#475569", bg: "#f1f5f9",
    desc: "Mantener relación",
  },
}

export const segmentoConfig = key => {
  const k = String(key || "").toLowerCase()
  return SEGMENTOS[k] || {
    label: key || "—", emoji: "·", color: "#64748b", bg: "#f1f5f9", desc: "",
  }
}

// Lista ordenada de segmentos para chips arriba
export const SEGMENTOS_ORDEN = [
  "champion", "loyal", "potential_loyal", "new", "promising",
  "needs_attention", "at_risk", "cant_lose", "hibernating", "lost", "regular",
]

// ───────── Sugerencias de mercadeo ─────────

export const sugerenciaConfig = tipo => {
  switch (String(tipo || "").toLowerCase()) {
    case "recordatorio":
      return { label: "Recordatorio", color: "#1e40af", bg: "#dbeafe", icon: "bell-outline" }
    case "upsell":
      return { label: "Upsell", color: "#065f46", bg: "#d1fae5", icon: "arrow-up-bold-circle-outline" }
    case "cross_sell":
      return { label: "Cross-sell", color: "#9a3412", bg: "#ffedd5", icon: "shuffle-variant" }
    case "retencion":
      return { label: "Retención", color: "#92400e", bg: "#fef3c7", icon: "shield-account-outline" }
    case "reactivacion":
      return { label: "Reactivación", color: "#991b1b", bg: "#fee2e2", icon: "refresh" }
    case "onboarding":
      return { label: "Onboarding", color: "#1e40af", bg: "#dbeafe", icon: "account-plus-outline" }
    case "fidelizacion":
      return { label: "Fidelización", color: "#065f46", bg: "#d1fae5", icon: "crown-outline" }
    default:
      return { label: tipo || "—", color: "#475569", bg: "#f1f5f9", icon: "lightbulb-outline" }
  }
}

export const prioridadConfig = p => {
  switch (String(p || "").toUpperCase()) {
    case "ALTA":  return { label: "Alta",  color: "#991b1b", bg: "#fee2e2" }
    case "MEDIA": return { label: "Media", color: "#92400e", bg: "#fef3c7" }
    case "BAJA":  return { label: "Baja",  color: "#1e40af", bg: "#dbeafe" }
    default:      return { label: p || "—", color: "#475569", bg: "#f1f5f9" }
  }
}

// ───────── Alertas ─────────

export const alertaConfig = tipo => {
  switch (String(tipo || "").toLowerCase()) {
    case "caida_compras":
      return { label: "Caída en compras", color: "#991b1b", bg: "#fee2e2", icon: "trending-down" }
    case "crecimiento_alto":
      return { label: "Crecimiento alto", color: "#065f46", bg: "#d1fae5", icon: "trending-up" }
    case "alta_devolucion":
      return { label: "Alta devolución", color: "#92400e", bg: "#fef3c7", icon: "undo-variant" }
    case "churn_critico":
      return { label: "Churn crítico", color: "#991b1b", bg: "#fee2e2", icon: "alert-octagon-outline" }
    default:
      return { label: tipo || "—", color: "#475569", bg: "#f1f5f9", icon: "alert-circle-outline" }
  }
}

// ───────── Utils RFM ─────────

export const scoreColor = score => {
  const n = Number(score) || 0
  if (n >= 5) return "#16a34a"
  if (n >= 4) return "#22c55e"
  if (n >= 3) return "#eab308"
  if (n >= 2) return "#f97316"
  return "#dc2626"
}

// ───────── Días de semana ─────────

export const DIAS_SEMANA_ORDEN = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"]

// Normaliza claves de día semana ("lunes", "Lunes", "Mon", etc.) al orden estándar
export const ordenarPorDia = obj => {
  if (!obj) return []
  const map = {}
  Object.entries(obj).forEach(([k, v]) => {
    const norm = String(k).trim().toLowerCase()
    map[norm] = v
  })
  return DIAS_SEMANA_ORDEN.map(d => ({
    dia: d,
    valor: map[d.toLowerCase()] ?? 0,
  }))
}

// ───────── Paleta general ─────────

export const PALETA = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
]
export const colorPara = i => PALETA[i % PALETA.length]

// ───────── Rango del mes ─────────

export const rangoDelMes = (year, month) => {
  const yyyy = String(year)
  const mm = String(month).padStart(2, "0")
  const ultimo = new Date(year, month, 0).getDate()
  return {
    fecha_inicio: `${yyyy}-${mm}-01`,
    fecha_fin: `${yyyy}-${mm}-${String(ultimo).padStart(2, "0")}`,
  }
}

// ───────── Order_by labels ─────────

export const ORDER_OPTIONS = [
  { value: "monetary",        label: "Importe (monetary)" },
  { value: "recency",         label: "Recency (más reciente)" },
  { value: "frequency",       label: "Frecuencia (más facturas)" },
  { value: "ticket_promedio", label: "Ticket promedio" },
  { value: "nombre",          label: "Nombre (A-Z)" },
]
